import React from "react";
import {
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
  Button,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
  Container,
  Paper,
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import ChatWindow from "./Helpers/ChatWindow";
import Navbar from "../../../globals/components/Navbar";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GoogleAutoComplete from "../../../globals/components/forms/GoogleAutoCompleteField";
import { toast } from "react-toastify";
import { BondNumberSelect } from "./Helpers/BondNumberSelect";
import useBlanketBondRetrieve from "./hooks/useBlanketBondRetrieve";
import BasicInformation from "./components/BasicInformation";
import DetailedInformation from "./components/DetailedInformation";
import BlanketObligeeInformation from "./components/BlanketObligeeInformation";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const VermostBlanketBondRetrieveForm = () => {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    errors,
    navigate,
    bond_id,
    role,
    disabled,
    pii_access,
    username,
    data,
    loaded,
    bondNum,
    pgId,
    pgData,
    allCities,
    allStates,
    allCounties,
    loadingCounties,
    obligees,
    loadingObligees,
    state,
    setState,
    billingMethod,
    setBillingMethod,
    isIndemnityReceived,
    setIsIndemnityReceived,
    editableCreatedAt,
    setEditableCreatedAt,
    openChat,
    setOpenChat,
    chatUnreadTotal,
    showSSN,
    setShowSSN,
    updateLoading,
    approveLoading,
    verificationLoading,
    rejectLoading,
    isUnderwritingLoading,
    isApproveLoading,
    isRejectLoading,
    isEsignatureLoading,
    isCreditUploading,
    setIsCreditUploading,
    rejectFcraUploading,
    setRejectFcraUploading,
    anchorEl,
    underwritingAnchorEl,
    renewalsAnchorEl,
    setRenewalsAnchorEl,
    rejectDialogOpen,
    setRejectDialogOpen,
    rejectFcraFile,
    setRejectFcraFile,
    isCreditDialogOpen,
    setIsCreditDialogOpen,
    creditReportFile,
    setCreditReportFile,
    openObligeeDialog,
    setOpenObligeeDialog,
    newObligee,
    setNewObligee,
    overrideReasonDialogOpen,
    setOverrideReasonDialogOpen,
    overrideReason,
    setOverrideReason,
    manualUnderwritingDialogOpen,
    setManualUnderwritingDialogOpen,
    manualUnderwritingReason,
    setManualUnderwritingReason,
    verificationCertDialogOpen,
    setVerificationCertDialogOpen,
    setVerificationCertFormData,
    verificationCertEffectiveDate,
    setVerificationCertEffectiveDate,
    verificationCertExpirationDate,
    setVerificationCertExpirationDate,
    verificationCertBondTerm,
    setVerificationCertBondTerm,
    BOND_TERM_OPTIONS,
    handleGoogleCallback,
    handleObligeeGoogleCallback,
    handleAddObligee,
    handleReject,
    onSubmit,
    handleUpdateUnderwriting,
    handleMenuClick,
    handleMenuClose,
    handleApproveOption,
    handleUnderwritingMenuClick,
    handleUnderwritingMenuClose,
    handleOverrideUnderwritingClick,
    handleManualUnderwritingClick,
    handleSubmitManualUnderwriting,
    handleSubmitOverrideReason,
    handleVerificationCertificate,
    handleVerificationCertConfirm,
    handleSignIndemnity,
    runCreditReport,
    uploadToADLS,
    updateBondNum,
  } = useBlanketBondRetrieve();
  if (!loaded) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ mt: 6, mb: 6 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold" }}
        >
          Guardianship Blanket Bond Request
        </Typography>

        {data?.underwriting_status &&
          data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
          (role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  color: ["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase())
                    ? "success.main"
                    : ["pending", "rejected"].includes(data.underwriting_status?.trim()?.toLowerCase())
                      ? "error.main"
                      : "warning.main",
                  mt: 0.25,
                }}
              >
                {["approved", "overrided"].includes(data.underwriting_status?.trim()?.toLowerCase()) ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <InfoOutlinedIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  Underwriting
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <div>
                    <span style={{ fontWeight: 700 }}>Status:</span>{" "}
                    <span style={{ fontWeight: 400, color: "inherit" }}>
                      {data.underwriting_status?.trim()?.toLowerCase() === "overrided"
                        ? (data.overrided_by?.trim() || data.overrided_at)
                            ? `Not Required (Overrided by ${(data.overrided_by || "").trim()} at ${data.overrided_at ? new Date(data.overrided_at).toLocaleString() : ""})`
                            : "Not Required"
                        : (() => { const s = data.underwriting_status?.trim()?.toLowerCase(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; })()}
                    </span>
                  </div>
                  {data.underwriting_status?.trim()?.toLowerCase() === "overrided" &&
                    (data.override_reason || "").trim() && (
                      <div><span style={{ fontWeight: 700 }}>Override reason:</span>{" "}<span style={{ fontWeight: 400 }}>{(data.override_reason || "").trim()}</span></div>
                    )}
                  {data.underwriting_status?.trim()?.toLowerCase() !== "overrided" &&
                    data.underwriting_status?.trim()?.toLowerCase() !== "not required" &&
                    data.underwriting_reason &&
                    data.underwriting_reason !== "N/A" && (
                      <div>Reason for Trigger: {data.underwriting_reason}</div>
                    )}
                </Typography>
              </Box>
            </Box>
          )}

        {pgData !== null &&
          pgData.ams_customer_number === null &&
          !disabled &&
          (role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                width: "100%",
                mb: 2,
                p: 2,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ color: "warning.main", mt: 0.25 }}>
                <WarningAmberIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  AMS customer number
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  The AMS Customer Number for this Professional Guardian is not set. System will automatically create a new customer with a customer number in AMS.
                </Typography>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigate(`/view_professional_guardian/${pgId}`);
                    }}
                  >
                    Enter Now
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Basic Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <BasicInformation
              isDisabled={disabled}
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              allCities={allCities}
              allStates={allStates}
              allCounties={allCounties}
              loadingCounties={loadingCounties}
              handleGoogleCallback={handleGoogleCallback}
              state={state}
              setState={setState}
              showSSN={showSSN}
              setShowSSN={setShowSSN}
              pii_access={pii_access}
              role={role}
              data={data}
              GoogleAutoComplete={GoogleAutoComplete}
            />
          </Paper>

          {/* Vertical Space */}
          <Box sx={{ mt: 4 }} />

          {/* Detailed Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DetailedInformation
                isDisabled={disabled}
                control={control}
                errors={errors}
                watch={watch}
                setValue={setValue}
                role={role}
                pii_access={pii_access}
              />
            </Box>
          </Paper>

          {/* Vertical Space */}
          <Box sx={{ mt: 4 }} />

          {/* Obligee Information Section */}
          <Paper
            elevation={3}
            sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
          >
            <BlanketObligeeInformation
              isDisabled={disabled}
              control={control}
              obligees={obligees}
              loadingObligees={loadingObligees}
              watch={watch}
              openObligeeDialog={openObligeeDialog}
              setOpenObligeeDialog={setOpenObligeeDialog}
              newObligee={newObligee}
              setNewObligee={setNewObligee}
              handleObligeeGoogleCallback={handleObligeeGoogleCallback}
              GoogleAutoComplete={GoogleAutoComplete}
              allCities={allCities}
              allStates={allStates}
              handleAddObligee={handleAddObligee}
            />
          </Paper>

          {!disabled && (
            <BondNumberSelect data={bondNum} updateData={updateBondNum} />
          )}

          {!disabled && (
            <Box
              sx={{
                maxWidth: 765,
                mx: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 3,
                mt: 3,
                mb: 3,
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                backgroundColor: "#f3f4f6", // bg-gray-100
              }}
            >
              <Typography variant="h5" className="font-bold mb-4">
                Billing Method
              </Typography>

              <RadioGroup
                row
                value={billingMethod}
                onChange={(e) => setBillingMethod(e.target.value)}
              >
                <FormControlLabel
                  value="Direct Bill"
                  control={<Radio />}
                  label="Direct Bill"
                  sx={{ mr: 4 }}
                />
                <FormControlLabel
                  value="Agency Bill"
                  control={<Radio />}
                  label="Agency Bill"
                />
              </RadioGroup>
            </Box>
          )}

          {/* Additional Information */}
          {(role === "super_user" || role === "internal_user") && (
            <Box
              sx={{
                maxWidth: 765,
                mx: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                padding: 3,
                mt: 3,
                mb: 3,
                boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                backgroundColor: "#f3f4f6", // bg-gray-100
              }}
            >
              <Typography variant="h5" className="font-bold mb-4">
                Additional Information
              </Typography>

              <TextField
                select
                label="Is Signed Indemnity Received"
                value={isIndemnityReceived}
                onChange={(e) => setIsIndemnityReceived(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              >
                <MenuItem value="">Not Selected</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Created At"
                type="datetime-local"
                value={editableCreatedAt}
                onChange={(e) => setEditableCreatedAt(e.target.value)}
                sx={{ mb: 2 }}
                slotProps={{ inputLabel: { shrink: true }}}
                helperText={data?.bond?.created_at || data?.created_at ? `Current: ${new Date(data?.created_at ?? data?.bond?.created_at).toLocaleString()}` : ""}
              />
            </Box>
          )}

          {/* Buttons */}
          {!disabled && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}
            >
              <Button
                endIcon={
                  updateLoading && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
                type="submit"
                variant="contained"
                color="primary"
              >
                Update
              </Button>

              <Button
                variant="contained"
                sx={{ backgroundColor: "#4caf50", color: "white" }}
                onClick={handleMenuClick}
                disabled={approveLoading}
                endIcon={
                  approveLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {approveLoading ? "Approving..." : "Approve"}
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    if (!billingMethod) {
                      toast.error(
                        "Please select a Billing Method before approving with invoice."
                      );
                      handleMenuClose();
                      return;
                    }
                    handleApproveOption({
                      approval: "approval",
                      billing_method: billingMethod,
                      ams_flow: true,
                    });
                  }}
                >
                  Approve with Policy
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleApproveOption({
                      billing_method: "Agency Bill",
                      ams_flow: false,
                    })
                  }
                >
                  Approve without Policy
                </MenuItem>
              </Menu>
              <Button
                endIcon={
                  rejectLoading && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
                type="button"
                variant="contained"
                sx={{ backgroundColor: "#f44336", color: "white" }}
                onClick={() => setRejectDialogOpen(true)}
              >
                Reject
              </Button>

              {/* Underwriting Dropdown */}
              <Button
                variant="contained"
                color="warning"
                onClick={handleUnderwritingMenuClick}
                disabled={isUnderwritingLoading}
                endIcon={
                  isUnderwritingLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {isUnderwritingLoading ? "Processing..." : "Underwriting"}
              </Button>

              <Menu
                anchorEl={underwritingAnchorEl}
                open={Boolean(underwritingAnchorEl)}
                onClose={handleUnderwritingMenuClose}
              >
                <MenuItem onClick={handleManualUnderwritingClick}>
                  Trigger Underwriting
                </MenuItem>
                <MenuItem onClick={handleOverrideUnderwritingClick}>
                  Override Underwriting
                </MenuItem>
                <MenuItem onClick={() => setIsCreditDialogOpen(true)}>
                  Upload Credit Report
                </MenuItem>
              </Menu>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#9c27b0",
                  color: "white",
                  "&:hover": { backgroundColor: "#7b1fa2" },
                }}
                onClick={(event) => setRenewalsAnchorEl(event.currentTarget)}
                disabled={verificationLoading}
                endIcon={
                  verificationLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <ArrowDropDownIcon />
                  )
                }
              >
                {verificationLoading ? "Processing Renewal..." : "Renewals"}
              </Button>

              <Menu
                anchorEl={renewalsAnchorEl}
                open={Boolean(renewalsAnchorEl)}
                onClose={() => setRenewalsAnchorEl(null)}
              >
                <MenuItem onClick={() => handleVerificationCertificate()}>
                  Verification Certificate
                </MenuItem>
              </Menu>

              {/* Verification Certificate – edit effective & expiration dates */}
              <Dialog
                open={verificationCertDialogOpen}
                onClose={() => {
                  if (!verificationLoading) {
                    setVerificationCertDialogOpen(false);
                    setVerificationCertFormData(null);
                  }
                }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Verification Certificate</DialogTitle>
                <DialogContent dividers sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Edit the bond term and effective/expiration dates to be sent in the payload. If you change the bond term, the bond will be updated first, then the verification certificate will be sent.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Bond Term</Typography>
                  <RadioGroup
                    row
                    value={verificationCertBondTerm}
                    onChange={(e) => setVerificationCertBondTerm(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    {BOND_TERM_OPTIONS.map((term) => (
                      <FormControlLabel
                        key={term}
                        value={term}
                        control={<Radio size="small" />}
                        label={term}
                      />
                    ))}
                  </RadioGroup>
                  <TextField
                    label="Effective Date"
                    type="date"
                    value={verificationCertEffectiveDate}
                    onChange={(e) => setVerificationCertEffectiveDate(e.target.value)}
                    fullWidth
                    margin="normal"
                    slotProps={{ inputLabel: { shrink: true }}}
                  />
                  <TextField
                    label="Expiration Date"
                    type="date"
                    value={verificationCertExpirationDate}
                    onChange={(e) => setVerificationCertExpirationDate(e.target.value)}
                    fullWidth
                    margin="normal"
                    slotProps={{ inputLabel: { shrink: true }}}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setVerificationCertDialogOpen(false);
                      setVerificationCertFormData(null);
                    }}
                    disabled={verificationLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleVerificationCertConfirm}
                    variant="contained"
                    color="primary"
                    disabled={verificationLoading || !verificationCertEffectiveDate || !verificationCertExpirationDate}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>

              <Button
                variant="contained"
                color="info"
                onClick={handleSignIndemnity}
                disabled={isEsignatureLoading || data?.bond?.status === "Active" || data?.bond?.third_document_url === null || watch("is_esignatures") === "No"}
                endIcon={isEsignatureLoading && <CircularProgress size={20} />}
              >
                {isEsignatureLoading ? "Signing..." : "Trigger Formstack"}
              </Button>
            </Box>
          )}

          {role === "carrier_team_member" && (
            <Box
              className="flex justify-center"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={() => handleUpdateUnderwriting("approved")}
                disabled={isApproveLoading || !data?.is_underwriting}
                endIcon={isApproveLoading && <CircularProgress size={20} />}
              >
                {isApproveLoading ? "Approving..." : "Approve"}
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={() => handleUpdateUnderwriting("rejected")}
                disabled={isRejectLoading || !data?.is_underwriting}
                endIcon={isRejectLoading && <CircularProgress size={20} />}
              >
                {isRejectLoading ? "Rejecting..." : "Reject"}
              </Button>
            </Box>
          )}

          {(role === "carrier_team_member" ||
            role === "super_user" ||
            role === "internal_user") && (
            <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1500 }}>
              <Badge
                overlap="circular"
                color="success"
                invisible={!chatUnreadTotal}
                badgeContent={chatUnreadTotal}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ '& .MuiBadge-badge': { top: -6, right: -6, zIndex: 2, transform: 'none', minWidth: 18, height: 18, fontSize: 11, fontWeight: 600, boxShadow: 1 } }}
              >
                <Fab
                  aria-label="chat"
                  sx={{ background: "linear-gradient(135deg, #111 0%, #333 100%)", color: "#fff", border: "1px solid #444", boxShadow: 6, "&:hover": { background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)" } }}
                  onClick={() => setOpenChat(!openChat)}
                >
                  {openChat ? (
                    <CloseIcon sx={{ color: "#fff" }} />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.2,
                      }}
                    >
                      <ChatIcon sx={{ color: "#fff" }} />
                      <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
                        UW
                      </Typography>
                    </Box>
                  )}
                </Fab>
              </Badge>
            </Box>
          )}
          {/* Render ChatWindow if openChat is true */}
          {openChat &&
            (role === "carrier_team_member" ||
              role === "super_user" ||
              role === "internal_user") && (
              <ChatWindow
                username={username}
                bondId={bond_id}
                role={role}
                onClose={() => setOpenChat(false)}
              />
            )}
        </form>
      </Container>
      <Dialog
        open={rejectDialogOpen}
        onClose={() => {
          if (!rejectFcraUploading) {
            setRejectDialogOpen(false);
            setRejectFcraFile(null);
          }
        }}
      >
        <DialogTitle>Reject Bond</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You may optionally attach an FCRA document before rejecting. You can also reject without uploading.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            disabled={rejectFcraUploading}
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: 2,
              borderColor: "rgba(37, 99, 235, 0.5)",
              color: "#2563eb",
              fontWeight: 600,
              textTransform: "uppercase",
              mb: 1,
              "&:hover": {
                borderColor: "#2563eb",
                backgroundColor: "rgba(37, 99, 235, 0.04)",
              },
            }}
          >
            Choose File
            <input
              type="file"
              hidden
              accept="application/pdf,.pdf,image/*"
              onChange={(e) => setRejectFcraFile(e.target.files?.[0] || null)}
              disabled={rejectFcraUploading}
            />
          </Button>
          {rejectFcraFile && (
            <Typography variant="body2" sx={{ display: "block", mt: 1, color: "text.primary" }}>
              Selected File: {rejectFcraFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setRejectFcraFile(null);
            }}
            color="secondary"
            disabled={rejectFcraUploading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleReject()}
            disabled={rejectLoading}
            sx={{ color: "#f44336", borderColor: "#f44336" }}
          >
            Reject without FCRA
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#f44336", color: "white" }}
            disabled={rejectLoading || rejectFcraUploading}
            onClick={async () => {
              if (rejectFcraFile) {
                setRejectFcraUploading(true);
                try {
                  const link = await uploadToADLS(rejectFcraFile);
                  await handleReject({
                    fcra_document: rejectFcraFile.name,
                    fcra_document_link: link,
                  });
                } catch (err) {
                  console.error(err);
                  toast.error("Upload failed. Rejecting without FCRA document.");
                  await handleReject();
                } finally {
                  setRejectFcraUploading(false);
                }
              } else {
                await handleReject();
              }
            }}
            endIcon={rejectFcraUploading && <CircularProgress size={20} color="inherit" />}
          >
            {rejectFcraUploading ? "Uploading..." : "Upload & Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreditDialogOpen}
        onClose={() => setIsCreditDialogOpen(false)}
      >
        <DialogTitle>Upload Credit Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Upload the credit report PDF to notify the underwriter.
          </Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              padding: "6px 12px",
              borderRadius: "4px",
              backgroundColor: "#fafafa",
              display: "inline-block",
              fontSize: "0.9rem",
              cursor: isCreditUploading ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => setCreditReportFile(e.target.files[0])}
              disabled={isCreditUploading}
              style={{ fontSize: "0.85rem", width: "100%" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsCreditDialogOpen(false)}
            color="secondary"
            disabled={isCreditUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!creditReportFile) {
                toast.error("Please upload a PDF file.");
                return;
              }
              setIsCreditUploading(true);
              try {
                await runCreditReport(bond_id, creditReportFile);
                toast.success("Credit report uploaded successfully.");
                setIsCreditDialogOpen(false);
                setCreditReportFile(null);
              } catch (error) {
                console.error(error);
                toast.error("Upload failed. Try again.");
              } finally {
                setIsCreditUploading(false);
              }
            }}
            disabled={isCreditUploading}
            endIcon={isCreditUploading && <CircularProgress size={20} />}
          >
            {isCreditUploading ? "Uploading..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual underwriting creation dialog */}
      <Dialog open={manualUnderwritingDialogOpen} onClose={() => setManualUnderwritingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Trigger Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason"
            placeholder="Enter reason for creating underwriting entry"
            value={manualUnderwritingReason}
            onChange={(e) => setManualUnderwritingReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(manualUnderwritingReason || "").trim()}
            helperText={!(manualUnderwritingReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManualUnderwritingDialogOpen(false); setManualUnderwritingReason(""); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmitManualUnderwriting} disabled={isUnderwritingLoading || !(manualUnderwritingReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Override underwriting reason dialog */}
      <Dialog open={overrideReasonDialogOpen} onClose={() => setOverrideReasonDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Underwriting</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for override"
            placeholder="Enter reason for overriding underwriting"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            error={!(overrideReason || "").trim()}
            helperText={!(overrideReason || "").trim() ? "Reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideReasonDialogOpen(false); setOverrideReason(""); }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSubmitOverrideReason} disabled={isUnderwritingLoading || !(overrideReason || "").trim()}>
            {isUnderwritingLoading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default VermostBlanketBondRetrieveForm;
